import {
  ArrowRightStartOnRectangleIcon,
  CalendarIcon,
} from "@heroicons/react/24/solid";

import { useLocation } from "react-router";
import useNav from "../../hooks/useNavigation";
import { useAuth } from "../../hooks/useAuth";
import { useUser } from "../../contexts/UserContext/hooks/useUser";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { GrAnnounce } from "react-icons/gr";

import Avatar from "../Avatar";
import {
  CubeIcon,
  StarIcon,
  UserGroupIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import Logo from "../Logo";
import { useState, lazy, Suspense, useEffect } from "react";
import { cn } from "../../lib/utils";
import { OrganizationType } from "../../__generated__/graphql";
import { FaFileInvoiceDollar } from "react-icons/fa";
import {
  MdOutlineRealEstateAgent,
  MdSwitchAccessShortcut,
} from "react-icons/md";
import { IoDocuments } from "react-icons/io5";
import { IoIosNotifications } from "react-icons/io";

// Lazy load the NotificationsCenter component
const NotificationsCenter = lazy(
  () => import("./components/NotificationsCenter")
);

interface Nav {
  name: string;
  path: string;
  icon: React.ReactNode;
  testId?: string;
}

const PRODUCTION_NAV_ITEMS: Nav[] = [
  {
    name: "Planning",
    path: "/dashboard",
    icon: <CalendarIcon className="w-4 h-4" />,
    testId: "nav-dashboard",
  },
  {
    name: "Projets",
    path: "/projects",
    icon: <CubeIcon className="w-4 h-4" />,
    testId: "nav-projects",
  },
  {
    name: "Évènements",
    path: "/events",
    icon: <StarIcon className="w-4 h-4" />,
    testId: "nav-events",
  },
  {
    name: "Artistes & Collab'",
    path: "/collaborators",
    icon: <UserGroupIcon className="w-4 h-4" />,
    testId: "nav-collaborators",
  },
];

const ARTIST_NAV_ITEMS: Nav[] = [
  {
    name: "Planning",
    path: "/dashboard",
    icon: <CalendarIcon className="w-4 h-4" />,
    testId: "nav-dashboard",
  },
  {
    name: "Évènements",
    path: "/events",
    icon: <StarIcon className="w-4 h-4" />,
    testId: "nav-events",
  },
  {
    name: "Communications",
    path: "/documents?documentType=communication",
    icon: <GrAnnounce className="w-4 h-4" />,
    testId: "nav-documents",
  },
  {
    name: "Fiches de paie",
    path: "/documents?documentType=fiche_de_paie",
    icon: <FaFileInvoiceDollar className="w-4 h-4" />,
  },
  {
    name: "Déclarations SACD",
    path: "/documents?documentType=declaration",
    icon: <MdOutlineRealEstateAgent className="w-4 h-4" />,
  },
  {
    name: "Autres documents",
    path: "/documents?documentType=other",
    icon: <IoDocuments className="w-4 h-4" />,
  },
  {
    name: "Collaborateurs",
    path: "/collaborators",
    icon: <UserGroupIcon className="w-4 h-4" />,
    testId: "nav-collaborators",
  },
];

export default function ConnectedSideNavBar() {
  const navigateWithLog = useNav();
  const { pathname } = useLocation();
  const { logout } = useAuth();
  const { userOrganization, currentOrganization, organizations } = useUser();

  // const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);
  const [showNotifications, setShowNotifications] = useState<boolean>(false);

  const organizationType = currentOrganization?.organizationType;

  // useHandleOpenCommandPalette(setIsSearchOpen);

  // Auto-load notifications after 3 seconds to avoid doing massive GraphQL queries at startup
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowNotifications(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const isNavItemActive = (nav: Nav) => {
    const searchParams = new URLSearchParams(window.location.search);
    const documentType = searchParams.get("documentType");

    if (documentType) {
      return nav.path.includes(`?documentType=${documentType}`);
    }

    return pathname === nav.path;
  };

  const navEntry = (nav: Nav) => {
    return (
      <div
        key={nav.path}
        data-testid={nav.testId}
        className={cn(
          "text-sm cursor-pointer flex items-center gap-2 hover:text-accent hover:bg-dark rounded-md p-2 border border-transparent select-none",
          isNavItemActive(nav)
            ? "text-accent bg-dark border border-input"
            : "text-primary"
        )}
        onClick={() => navigateWithLog(nav.path)}
      >
        {nav.icon}
        {nav.name}
      </div>
    );
  };

  const userMenu = () => {
    return (
      <Menu as="div" data-testid="user-menu">
        <MenuButton className="cursor-pointer flex items-center gap-2 rounded-md px-2 py-4 border border-border w-full hover:bg-dark">
          <Avatar
            userOrganization={userOrganization}
            shape="circle"
            size="medium"
            className="border border-border"
          />
          <div className="flex flex-col items-start min-w-0 flex-1 text-left">
            <div className="text-xs font-bold text-primary truncate w-full">
              {userOrganization?.name}
            </div>
            <div className="text-xs text-secondary truncate w-full">
              {userOrganization?.email}
            </div>
            <div className="text-xs text-accent truncate w-full">
              {currentOrganization?.name}
            </div>
          </div>
        </MenuButton>

        <MenuItems
          transition
          className="p-2 absolute bottom-18 z-10 mt-2 w-60 origin-top rounded-t-md bg-card border border-border shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
        >
          <MenuItem>
            <div
              className={menuItemStyle}
              data-testid="user-menu-item-account"
              onClick={() => {
                navigateWithLog("/account");
              }}
            >
              <UserIcon className="w-4 h-4" />
              Compte
            </div>
          </MenuItem>
          {organizations && organizations.length > 1 && (
            <MenuItem>
              <div
                className={menuItemStyle}
                data-testid="user-menu-item-switch-org"
                onClick={() => {
                  navigateWithLog("/switch-organization");
                }}
              >
                <MdSwitchAccessShortcut className="w-4 h-4" />
                Changer d'organisation
              </div>
            </MenuItem>
          )}
          <MenuItem>
            <div
              data-testid="user-menu-item-logout"
              className={menuItemStyle}
              onClick={() => {
                logout();
              }}
            >
              <ArrowRightStartOnRectangleIcon className="w-4 h-4" />
              Déconnexion
            </div>
          </MenuItem>
        </MenuItems>
      </Menu>
    );
  };

  const menuItemStyle =
    "flex items-center gap-2 cursor-pointer px-2 py-1 text-primary hover:bg-quaternary hover:text-secondary rounded-md text-sm";

  const handleNavBarPerOrganizationType = () => {
    if (!organizationType) {
      // TODO: Loading state
      return null;
    }

    if (organizationType === OrganizationType.Artist) {
      return ARTIST_NAV_ITEMS;
    }

    // TODO: Implement other organization types
    return PRODUCTION_NAV_ITEMS;
  };

  return (
    <>
      {/* <Search isOpen={isSearchOpen} setIsOpen={setIsSearchOpen} /> */}

      <div className="flex flex-col gap-4 px-2 py-4 w-64 h-screen bg-background">
        <div className="flex items-center justify-between">
          <Logo className="text-lg text-primary px-2" betaBidgeSize="small" />

          <div className="flex items-center gap-2">
            {/* <MagnifyingGlassIcon
              className="w-6 h-6 text-secondary cursor-pointer hover:text-accent transition-all duration-300"
              onClick={() => setIsSearchOpen(true)}
            /> */}

            {showNotifications ? (
              <Suspense fallback={<div></div>}>
                <NotificationsCenter />
              </Suspense>
            ) : (
              <div className="relative">
                <IoIosNotifications className="text-secondary h-6 w-6" />
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col justify-between flex-1">
          <div className="flex flex-col gap-2">
            {handleNavBarPerOrganizationType()?.map((nav) => navEntry(nav))}
          </div>

          {userMenu()}
        </div>
      </div>
    </>
  );
}
